import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsAccordionShadow = 'none' | 'l1' | 'l2' | 'l3';
export type DsAccordionAppearance = 'contained' | 'minimal';

@Component({
  selector: 'ds-accordion',
  standalone: true,
  imports: [CommonModule, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-accordion"
      [class]="hostClasses"
      [style.--ds-accordion-border-color]="borderColor"
    >
      <button
        class="ds-accordion__header"
        type="button"
        [class.ds-accordion__header--expanded]="expanded"
        [attr.aria-expanded]="expanded"
        [disabled]="disabled"
        (click)="toggle()"
      >
        <span class="ds-accordion__header-content">
          <ng-content select="[ds-accordion-header]" />
        </span>
        <ds-icon
          class="ds-accordion__chevron"
          name="keyboard_arrow_down"
          [size]="'var(--_accordion-icon-size)'"
        ></ds-icon>
      </button>

      @if (expanded) {
        <div class="ds-accordion__content">
          <ng-content select="[ds-accordion-content]" />
          <ng-content />
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      color: var(--ds-accordion-content-color);
      font-family: 'Sura Sans', sans-serif;
    }

    .ds-accordion {
      --_accordion-background: var(--ds-accordion-background);
      --_accordion-header-background: var(--ds-accordion-header-background);
      --_accordion-hover-overlay: var(--ds-accordion-hover-overlay);
      --_accordion-title-color: var(--ds-accordion-title-color);
      --_accordion-content-color: var(--ds-accordion-content-color);
      --_accordion-icon-color: var(--ds-accordion-icon-color);
      --_accordion-border-radius: var(--ds-accordion-border-radius);
      --_accordion-title-font-size: var(--ds-accordion-title-font-size);
      --_accordion-title-line-height: var(--ds-accordion-title-line-height);
      --_accordion-title-font-weight: var(--ds-accordion-title-font-weight);
      --_accordion-header-padding-x: var(--ds-accordion-header-padding-x);
      --_accordion-header-padding-y: var(--ds-accordion-header-padding-y);
      --_accordion-header-gap: var(--ds-accordion-header-gap);
      --_accordion-content-padding-x: var(--ds-accordion-content-padding-x);
      --_accordion-content-padding-y: var(--ds-accordion-content-padding-y);
      --_accordion-content-gap: var(--ds-accordion-content-gap);
      --_accordion-icon-size: var(--ds-accordion-icon-size);
      --_accordion-divider-width: var(--ds-accordion-divider-width);
      display: block;
      overflow: hidden;
      border-radius: var(--_accordion-border-radius);
      background: var(--_accordion-background);
      box-sizing: border-box;
      transition: box-shadow var(--ds-accordion-hover-transition-duration) ease;
    }

    .ds-accordion--appearance-minimal {
      --_accordion-background: var(--ds-accordion-minimal-background);
      --_accordion-header-background: var(--ds-accordion-minimal-background);
      --_accordion-hover-overlay: var(--ds-accordion-minimal-hover-overlay);
      --_accordion-title-color: var(--ds-accordion-minimal-title-color);
      --_accordion-content-color: var(--ds-accordion-minimal-content-color);
      --_accordion-icon-color: var(--ds-accordion-minimal-icon-color);
      --_accordion-border-radius: var(--ds-accordion-minimal-border-radius);
      --_accordion-title-font-size: var(--ds-accordion-minimal-title-font-size);
      --_accordion-title-line-height: var(--ds-accordion-minimal-title-line-height);
      --_accordion-title-font-weight: var(--ds-accordion-minimal-title-font-weight);
      --_accordion-header-padding-x: var(--ds-accordion-minimal-header-padding-x);
      --_accordion-header-padding-y: var(--ds-accordion-minimal-header-padding-y);
      --_accordion-header-gap: var(--ds-accordion-minimal-header-gap);
      --_accordion-content-padding-x: var(--ds-accordion-minimal-content-padding-x);
      --_accordion-content-padding-y: var(--ds-accordion-minimal-content-padding-y);
      --_accordion-content-gap: var(--ds-accordion-minimal-content-gap);
      --_accordion-icon-size: var(--ds-accordion-minimal-icon-size);
      --_accordion-divider-width: var(--ds-accordion-minimal-divider-width);
      overflow: visible;
    }

    .ds-accordion--shadow-l1 {
      box-shadow: var(--ds-shadow-l1);
    }

    .ds-accordion--shadow-l2 {
      box-shadow: var(--ds-shadow-l2);
    }

    .ds-accordion--shadow-l3 {
      box-shadow: var(--ds-shadow-l3);
    }

    .ds-accordion--shadow-none {
      box-shadow: none;
    }

    .ds-accordion--bordered {
      border: var(--ds-accordion-divider-width) solid var(--ds-accordion-border-color);
    }

    .ds-accordion--borderless {
      border: 0;
    }

    .ds-accordion__header {
      position: relative;
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      gap: var(--_accordion-header-gap);
      margin: 0;
      padding: var(--_accordion-header-padding-y) var(--_accordion-header-padding-x);
      border: 0;
      border-radius: var(--_accordion-border-radius);
      background: var(--_accordion-header-background);
      color: var(--_accordion-title-color);
      cursor: pointer;
      font-family: inherit;
      font-size: var(--_accordion-title-font-size);
      font-weight: var(--_accordion-title-font-weight);
      line-height: var(--_accordion-title-line-height);
      outline: none;
      text-align: left;
    }

    .ds-accordion--appearance-minimal .ds-accordion__header {
      width: fit-content;
      max-width: 100%;
      min-height: var(--ds-accordion-minimal-header-height);
      justify-content: flex-start;
    }

    .ds-accordion__header::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: var(--_accordion-hover-overlay);
      opacity: 0;
      pointer-events: none;
      transition: opacity 120ms ease;
    }

    .ds-accordion__header:hover:not(:disabled)::before,
    .ds-accordion__header:active:not(:disabled)::before {
      opacity: 1;
    }

    .ds-accordion--appearance-contained .ds-accordion__header--expanded {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }

    .ds-accordion__header:focus-visible {
      z-index: 1;
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: calc(-1 * var(--ds-focus-stroke));
    }

    .ds-accordion--appearance-minimal .ds-accordion__header:focus-visible {
      outline-offset: var(--ds-focus-gap);
    }

    .ds-accordion__header:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }

    .ds-accordion__header-content {
      position: relative;
      z-index: 1;
      display: flex;
      flex: 1 1 0;
      min-width: 0;
      align-items: center;
      gap: var(--_accordion-header-gap);
      color: var(--_accordion-title-color);
    }

    .ds-accordion--appearance-minimal .ds-accordion__header-content {
      flex: 0 1 auto;
    }

    :host ::ng-deep [ds-accordion-header] {
      display: flex;
      min-width: 0;
      flex: 1 1 0;
      align-items: center;
      gap: var(--_accordion-header-gap);
      color: var(--_accordion-title-color);
      overflow-wrap: anywhere;
    }

    :host ::ng-deep .ds-accordion--appearance-minimal [ds-accordion-header] {
      flex: 0 1 auto;
    }

    .ds-accordion__chevron {
      position: relative;
      z-index: 1;
      color: var(--_accordion-icon-color);
    }

    .ds-accordion__chevron {
      transition: transform var(--ds-accordion-hover-transition-duration) ease;
    }

    .ds-accordion__header--expanded .ds-accordion__chevron {
      transform: rotate(180deg);
    }

    .ds-accordion__content {
      display: flex;
      flex-direction: column;
      gap: var(--_accordion-content-gap);
      padding: var(--_accordion-content-padding-y) var(--_accordion-content-padding-x);
      border-top: var(--_accordion-divider-width) solid var(--ds-accordion-divider-color);
      color: var(--_accordion-content-color);
      overflow-wrap: anywhere;
    }

    .ds-accordion--appearance-minimal .ds-accordion__content {
      margin-top: var(--ds-accordion-minimal-section-gap);
    }

    :host ::ng-deep [ds-accordion-content] {
      display: contents;
    }
  `],
})
export class DsAccordionComponent {
  private isExpanded = true;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @Input() disabled = false;
  @Input() appearance: DsAccordionAppearance = 'contained';
  @Input() shadow: DsAccordionShadow = 'l1';
  @Input() border: boolean | string = true;
  @Input() borderColor = 'var(--ds-accordion-divider-color)';

  @Input()
  get expanded(): boolean {
    return this.isExpanded;
  }
  set expanded(value: boolean | string) {
    this.isExpanded = value === '' || value === true || value === 'true';
  }

  @Output() expandedChange = new EventEmitter<boolean>();

  get hasBorder(): boolean {
    return this.border === true || this.border === '' || this.border === 'true';
  }

  get hostClasses(): string {
    const resolvedShadow = this.appearance === 'minimal' ? 'none' : this.shadow;
    const hasResolvedBorder = this.appearance === 'contained' && this.hasBorder;

    return [
      'ds-accordion',
      `ds-accordion--appearance-${this.appearance}`,
      `ds-accordion--shadow-${resolvedShadow}`,
      hasResolvedBorder ? 'ds-accordion--bordered' : 'ds-accordion--borderless',
    ].join(' ');
  }

  toggle(): void {
    if (this.disabled) return;
    this.isExpanded = !this.isExpanded;
    this.expandedChange.emit(this.isExpanded);
  }

  collapse(): void {
    if (!this.isExpanded) return;
    this.isExpanded = false;
    this.expandedChange.emit(false);
    this.cdr.markForCheck();
  }
}
